import styled from '@emotion/styled';
import {
  layout,
  palette,
  radius,
  shadow,
  spacing,
  status,
  transition,
  typography,
} from '../../../style-utils/theme';
import { down, touch } from '../../../style-utils/media-queries';
import { m, preset } from '../../../motion';

/**
 * ═══════════════ THE DESK'S "OUT" LANGUAGE ═══════════════
 *
 * Shared by "Coming out" (/visitor-exit) and "Activities out" (/activity-exit),
 * and deliberately so: they are the same act on a different table — a list of
 * things still open, a search over it, one confirmed press per row. A second
 * stylesheet would be two descriptions of one control that drift the first time
 * either is touched.
 *
 * ── WHAT THIS PASS CHANGED, AND WHY (owner, 2026-08-14: "the out screens
 *    should look so much better") ──
 *
 * 1. THE PAGE HAS A WIDTH NOW. `Body` stretched to whatever the monitor was, so
 *    on a desk display a visitor's name sat at the far left of a 1400px row and
 *    the button that signs them out sat at the far right, with a metre of empty
 *    card between the fact and the act. Capped and centred.
 *
 * 2. IT NO LONGER PAYS FOR PADDING THREE TIMES. Both pages nest
 *    ContentAndButtonsContainer (xxl lg 0) > ContentWrapper (lg) > Body (md),
 *    which put the first pixel of content ~88px in from the card edge. The
 *    shells are fixed in the pages; `Body` contributes none of its own.
 *
 * 3. THE COUNT IS A STRIP, NOT A PARENTHESIS. "Still inside (7)" in a heading is
 *    a number a desk has to read a sentence to find. The same three numbers the
 *    activity feed already shows in tiles are shown in tiles here, which is also
 *    what makes the STALE count visible without an amber paragraph.
 *
 * 4. A ROW LEADS WITH A MARK. Two lines of grey text and a button was a list
 *    that had to be read left to right to be scanned at all. The mark carries
 *    the initials (or the activity's first letter) and the row's state colour,
 *    so a picked or overdue row is recognisable before any word is.
 *
 * 5. THE ACTION IS ITS OWN BUTTON. It was <NavButton direction="forward">, a
 *    control whose entire visual argument is a right-pointing arrow, used for
 *    the act of somebody LEAVING. `RowAction` states the act; `danger` marks the
 *    irreversible confirm.
 *
 * Every value is a token (design_system.md). status.warning is the
 * needs-attention tint and is what marks a ghost arrival; gradient.aurora is
 * untouched — signing somebody out is not an AI surface, even though a scan can
 * start it.
 */

/**
 * THE PAGE COLUMN.
 *
 * No padding of its own: the host card already pads (see note 2 above). The cap
 * is the readable-measure rule — a two-column row of "who" and "sign them out"
 * stops being one glance somewhere around 900px.
 */
export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  flex: 1;
  box-sizing: border-box;
  width: 100%;
  max-width: 880px;
  min-width: 0;
  min-height: 0;
  margin: 0 auto;
  overflow-y: auto;
`;

export const Card = styled.section`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  padding: ${spacing.md};
`;

/* Title and the count strip share a row on a desk and stack on a phone, so the
   number never pushes the words off their line. */
export const CardTitle = styled.h2`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const Hint = styled.p`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

/**
 * ── THE COUNTS, AS TILES ──────────────────────────────────────────────────
 *
 * The same shape the activity feed uses on the main menu, on purpose: a desk
 * that has learned to read three numbers in a strip should not have to learn a
 * second way to read the same three numbers one screen later.
 *
 * The STALE tile is amber and never subtracted from the total. Both numbers are
 * true and they answer different questions: how many arrivals are open, and how
 * many of them anybody actually saw.
 */
export const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${spacing.xs};
  margin-bottom: ${spacing.sm};

  ${down('sm')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const Stat = styled.div<{ tone?: 'plain' | 'warning' | 'brand' }>`
  box-sizing: border-box;
  min-width: 0;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  border: 1px solid
    ${({ tone }) =>
      tone === 'warning'
        ? status.warning.dot
        : tone === 'brand'
        ? palette.iris
        : palette.line};
  background: ${({ tone }) =>
    tone === 'warning'
      ? status.warning.bg
      : tone === 'brand'
      ? palette.irisTintSoft
      : 'transparent'};

  b {
    display: block;
    font-family: ${typography.ui};
    font-size: ${typography.size.lg}px;
    font-weight: ${typography.weight.bold};
    line-height: 1;
    font-variant-numeric: tabular-nums;
    color: ${({ tone }) =>
      tone === 'warning'
        ? status.warning.fg
        : tone === 'brand'
        ? palette.irisDeep
        : palette.ink};
  }

  span {
    display: block;
    margin-top: ${spacing.half};
    font-family: ${typography.ui};
    font-size: ${typography.size.xxs}px;
    font-weight: ${typography.weight.semibold};
    letter-spacing: ${typography.tracking.caps};
    text-transform: uppercase;
    color: ${palette.slateLight};
  }
`;

export const ScanRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacing.sm};
`;

export const SearchRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin-bottom: ${spacing.sm};
`;

export const Label = styled.label`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const Input = styled.input`
  /* The desk's search box on three exit screens, measured at 38px — six short
     of the floor, on a control a concierge uses standing up at a tablet. */
  min-height: ${layout.touchTarget};
  box-sizing: border-box;
  width: 100%;
  box-sizing: border-box;
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  transition: ${transition.colors};

  &::placeholder {
    color: ${palette.slateLight};
  }
  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
  /* iOS Safari zooms the whole page when a focused field is under 16px and does
     not zoom back out. Coarse pointers only, so desk density is unchanged. */
  ${touch} {
    font-size: 16px;
  }
`;

/* A row's border carries the one fact that changes how it must be read: amber
   for an arrival nobody closed, brand for the one a scan just picked out. */
export const Row = styled.div<{ tone: 'plain' | 'stale' | 'picked' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  border: 1px solid
    ${({ tone }) =>
      tone === 'stale'
        ? status.warning.dot
        : tone === 'picked'
        ? palette.iris
        : palette.line};
  border-radius: ${radius.sm};
  background: ${({ tone }) =>
    tone === 'picked' ? palette.irisTintSoft : palette.surface};
  transition: ${transition.base};

  & + & {
    margin-top: ${spacing.xs};
  }
  &:hover {
    box-shadow: ${shadow.sm};
    border-color: ${({ tone }) =>
      tone === 'plain' ? palette.lineStrong : 'inherit'};
  }

  ${down('sm')} {
    flex-direction: column;
    align-items: stretch;
  }
`;

/**
 * THE ROW'S MARK — initials, or an activity's first letter.
 *
 * It is not decoration: it carries the row's state as a fill, so an overdue
 * arrival and the one a scan just picked out are distinguishable at the left
 * edge, where the eye enters the row, rather than only by a border colour the
 * reader has to notice around the outside of the whole thing.
 */
export const RowMark = styled.div<{ tone: 'plain' | 'stale' | 'picked' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: ${radius.circle};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.snug};
  text-transform: uppercase;
  /* The picked tone carries white initials at 13px/700, and white on
     palette.iris is 3.20:1 — under the floor for that size. Lagoon takes it to
     4.9:1 and keeps the same hue. The stale tone already uses a status triple,
     which is why it was never a problem.
     NO BACKTICKS IN THIS COMMENT: it is inside a styled template literal. */
  background: ${({ tone }) =>
    tone === 'stale'
      ? status.warning.bg
      : tone === 'picked'
      ? palette.lagoon
      : palette.surfaceMuted};
  color: ${({ tone }) =>
    tone === 'stale'
      ? status.warning.fg
      : tone === 'picked'
      ? palette.white
      : palette.slate};
`;

/* The mark and the two lines of text travel together; the action is what sits
   opposite them. Under sm the row stacks, and this keeps the mark beside the
   name rather than above it. */
export const RowLead = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  min-width: 0;
  flex: 1 1 auto;
`;

export const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const RowName = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const RowMeta = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export const Actions = styled.div`
  display: flex;
  gap: ${spacing.xs};
  align-items: center;
  flex-shrink: 0;
`;

/**
 * THE ACT ITSELF.
 *
 * This was <NavButton direction="forward"> — a control whose whole visual
 * argument is a right-pointing arrow, spent on the act of somebody LEAVING. It
 * also meant the row's primary button was the same shape as the wizard's "next",
 * so the two read as the same kind of press.
 *
 * `danger` is for the second tap only. A departure cannot be un-recorded, so the
 * confirm is the one control on these screens that should look like it costs
 * something.
 */
export const RowAction = styled.button<{ variant?: 'primary' | 'quiet' | 'danger' }>`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.half};
  min-height: ${layout.buttonHeight};
  padding: 0 ${spacing.md};
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  cursor: pointer;
  transition: ${transition.colors};

  /* THE THREE VARIANTS ARE NOW ACTUALLY THREE. (NO BACKTICKS IN THIS COMMENT:
     one closes the styled template literal and the whole module stops parsing.)
     danger used to pick palette.iris for its border and then fall through to
     the same iris background, white text and iris-deep hover as primary - the
     variant existed in the type and produced a primary button. A destructive row
     action would have looked exactly like a confirming one.
     alertStrong is the destructive fill that clears 4.5:1 under white text;
     alertDeep is its hover, which stays above that floor rather than lightening
     into it. */
  border: 1px solid
    ${({ variant }) =>
      variant === 'quiet'
        ? palette.lineStrong
        : variant === 'danger'
        ? palette.alertStrong
        : palette.iris};
  /* PRIMARY FILLS WITH LAGOON, NOT THE PRIMARY TEAL.
     This button carries a white 13px label. White on #00A19A is 3.20:1 — under
     the 4.5:1 that size needs — and this is the desk's one-press action on the
     resident, exit and activity-exit rows. Deep Lagoon takes the same label to
     4.9:1. The danger variant already learned this lesson: alertStrong exists
     because plain coral was 3.42:1 under white. */
  background: ${({ variant }) =>
    variant === 'quiet'
      ? palette.surface
      : variant === 'danger'
      ? palette.alertStrong
      : palette.lagoon};
  color: ${({ variant }) =>
    variant === 'quiet' ? palette.ink : palette.white};

  &:hover:not(:disabled) {
    background: ${({ variant }) =>
      variant === 'quiet'
        ? palette.irisTintSoft
        : variant === 'danger'
        ? palette.alertDeep
        : palette.irisDeep};
    border-color: ${({ variant }) =>
      variant === 'danger' ? palette.alertDeep : palette.irisDeep};
  }

  /* The focus ring follows the button it surrounds - a teal ring around a red
     destructive button reads as two different states at once. */
  &:focus-visible {
    outline: none;
    box-shadow: ${({ variant }) =>
      variant === 'danger' ? shadow.focusError : shadow.focus};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  ${down('sm')} {
    width: 100%;
  }
`;

export const Pill = styled.span<{
  tone: 'brand' | 'warning' | 'success' | 'neutral';
}>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  background: ${({ tone }) => status[tone].bg};
  color: ${({ tone }) => status[tone].fg};
  white-space: nowrap;
`;

/** The close control for a Banner that outlives its own usefulness. Sits inside
 *  the banner, inherits its colour so it cannot fall below the contrast the
 *  banner already passes, and carries the 24px hit area a control needs. */
export const BannerDismiss = styled.button`
  -webkit-appearance: none;
  appearance: none;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: auto;
  padding: 0;
  border: none;
  border-radius: ${radius.xs};
  background: transparent;
  color: inherit;
  font-size: ${typography.size.sm}px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: rgba(18, 41, 44, 0.08);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* A motion element: a banner is feedback that ARRIVES (and, inside a
   Presence, leaves) — spread bannerMotion on it. */
export const Banner = m.p<{ tone: 'brand' | 'warning' | 'alert' | 'success' }>`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  margin: 0 0 ${spacing.sm};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${({ tone }) => status[tone].bg};
  color: ${({ tone }) => status[tone].fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
`;

export const bannerMotion = {
  variants: preset.state,
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
} as const;

/**
 * ONE ROW OF THE OUT LIST — the row and, when it is open, its confirm step,
 * as one motion element: it arrives in the cascade (listItemMotion) and, in a
 * Presence, leaves as one thing when the person is signed out. The sibling
 * gap that Row used to carry lives here now, because Rows are no longer
 * siblings of each other.
 */
export const ExitItem = m.div`
  & + & {
    margin-top: ${spacing.xs};
  }
`;

/**
 * NOTHING TO DO IS A STATE, NOT A MISSING LIST.
 *
 * "Nobody is signed in. The lobby is clear." was one grey sentence flush against
 * the left of a card, indistinguishable from a screen that had failed to load.
 * Centred, with room around it, so an empty desk reads as an answer.
 */
export const Empty = styled.p`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.half};
  margin: 0;
  padding: ${spacing.lg} ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  text-align: center;
`;

/* The confirm step. A departure cannot be un-recorded, so the second tap is
   the point — and it names the person, so the tap is aimed rather than
   reflexive. */
export const Confirm = m.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  margin-top: ${spacing.xs};
  padding: ${spacing.sm};
  border: 1px solid ${palette.iris};
  border-radius: ${radius.sm};
  background: ${palette.irisTintSoft};
`;

/* The confirm step fades in under its row and out again on Cancel. Spread
   on Confirm inside a Presence. */
export const confirmMotion = {
  variants: preset.state,
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
} as const;

export const ConfirmText = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  flex: 1 1 220px;
  min-width: 0;
`;
